
import { v4 as uuidv4 } from "uuid";
import { Candidate } from "@/types/candidate";
import { supabase } from "@/integrations/supabase/client";
import { extractTextFromFile } from "./cv-extractor";

export interface CandidateFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  skills: string;
  summary: string;
  linkedin?: string;
  cvFile: File | null;
  linkedinJson?: string; // Added to store the full LinkedIn JSON data
}

export async function createCandidate(
  formData: CandidateFormData & { name: string },
  updateProgress?: (stage: string | null, progress: number) => void
): Promise<Candidate | null> {
  try {
    updateProgress && updateProgress("Creating candidate record", 10);

    // Extract skills array from comma-separated string
    const skills = formData.skills
      ? formData.skills.split(",").map((skill) => skill.trim())
      : [];

    // Create the basic candidate record
    const { data: candidateData, error: candidateError } = await supabase
      .from("candidates")
      .insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        function: formData.role,
        skills: skills,
        status: "New",
        linkedin: formData.linkedin,
        linkedin_json: formData.linkedinJson ? JSON.parse(formData.linkedinJson) : null
      })
      .select()
      .single();

    if (candidateError) {
      throw new Error(`Error creating candidate: ${candidateError.message}`);
    }

    updateProgress && updateProgress("Candidate record created", 40);

    const candidateId = candidateData.id;

    // If there's a CV file, process it
    if (formData.cvFile) {
      try {
        updateProgress && updateProgress("Uploading CV file", 50);
        
        // Extract text from the CV
        const textContent = await extractTextFromFile(formData.cvFile);
        
        updateProgress && updateProgress("Processing CV content", 70);
        
        // Generate a unique filename
        const timestamp = Date.now();
        const fileExt = formData.cvFile.name.split('.').pop();
        const fileName = `${candidateId}_${timestamp}.${fileExt}`;
        const filePath = `candidates/${candidateId}/${fileName}`;
        
        // Upload the file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(filePath, formData.cvFile);
          
        if (uploadError) {
          console.error("Error uploading CV:", uploadError);
        }
        
        updateProgress && updateProgress("Analyzing CV content", 80);
        
        // Call the analyze-resume function with the text content
        const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
          'analyse-resume',
          {
            body: {
              candidateId,
              cvContent: textContent,
              notes: formData.summary || ""
            }
          }
        );
        
        if (analysisError) {
          console.error("Error analyzing resume:", analysisError);
        }
        
        updateProgress && updateProgress("CV processing complete", 90);
      } catch (error) {
        console.error("Error processing CV:", error);
      }
    } else if (formData.summary) {
      // If there's a summary but no CV, still update the candidate record
      const { error: updateError } = await supabase
        .from("candidates")
        .update({
          ai_synthesis: formData.summary
        })
        .eq("id", candidateId);
        
      if (updateError) {
        console.error("Error updating candidate with summary:", updateError);
      }
    }
    
    // Add an entry to candidate history
    await supabase.from("candidate_history").insert({
      candidate_id: candidateId,
      action: "CREATE",
      description: "Candidate created",
      performed_by: (await supabase.auth.getUser()).data.user?.id || "Unknown"
    });

    updateProgress && updateProgress("Candidate created successfully", 100);
    
    // Fetch the complete candidate record
    const { data: finalCandidateData, error: fetchError } = await supabase
      .from("candidates")
      .select("*")
      .eq("id", candidateId)
      .single();
      
    if (fetchError) {
      console.error("Error fetching final candidate data:", fetchError);
      return candidateData as Candidate;
    }
    
    return finalCandidateData as Candidate;
  } catch (error) {
    console.error("Error in createCandidate:", error);
    updateProgress && updateProgress(null, 0);
    return null;
  }
}
